import React from 'react';
import { X, Edit, Shield } from 'lucide-react';
import { User as UserType } from '../../types';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

interface UserDetailModalProps {
  isOpen: boolean;
  user: UserType | null;
  branches: any[];
  units: any[];
  onClose: () => void;
  onOpenEdit: () => void;
  onOpenSecurity: () => void;
}

const UserDetailModal: React.FC<UserDetailModalProps> = ({
  isOpen,
  user,
  branches,
  units,
  onClose,
  onOpenEdit,
  onOpenSecurity
}) => {
  if (!isOpen || !user) return null;

  const getRoleText = (role: UserType['role']) => {
    switch (role) {
      case 'admin': return 'Sistem Yöneticisi';
      case 'branch': return 'Şube Yöneticisi';
      case 'unit': return 'Birim Çalışanı';
      case 'student': return 'Öğrenci';
      default: return role;
    }
  };

  const getUnitTypeName = (type: string): string => {
    switch (type) {
      case 'education_consultant': return 'Eğitim Danışmanı';
      case 'visa_consultant': return 'Vize Danışmanı';
      case 'marketing': return 'Pazarlama';
      case 'accounting': return 'Muhasebe';
      case 'customer_service': return 'Müşteri Hizmetleri';
      case 'sales': return 'Satış';
      case 'testprep': return 'Test Hazırlık';
      case 'teknik_destek': return 'Teknik Destek';
      default: return type;
    }
  };

  const getBranchName = (branchId?: string) => {
    if (!branchId) return '-';
    const branch = branches.find(b => b.id === branchId);
    return branch ? branch.name : 'Bilinmeyen Şube';
  };

  const getUnitName = (unitId?: string) => {
    if (!unitId) return '-';
    const unit = units.find(u => u.id === unitId);
    return unit ? unit.name : 'Bilinmeyen Birim';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
              <span className="text-white text-lg font-semibold">
                {user.name ? user.name.split(' ').map(n => n[0]).join('') : 'U'}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{user.name}</h2>
              <p className="text-gray-600 dark:text-gray-400">{getRoleText(user.role)}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Temel Bilgiler</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Email</p>
                  <p className="font-medium text-gray-900 dark:text-white">{user.email}</p>
                </div>
                {user.phone && (
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Telefon</p>
                    <p className="font-medium text-gray-900 dark:text-white">{user.phone}</p>
                  </div>
                )}
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Durum</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'}`}>
                    {user.isActive ? 'Aktif' : 'Pasif'}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Organizasyon</h3>
              <div className="space-y-3 text-sm">
                {user.branchId && (
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Şube</p>
                    <p className="font-medium text-gray-900 dark:text-white">{getBranchName(user.branchId)}</p>
                  </div>
                )}
                {user.unitId && (
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Birim</p>
                    <p className="font-medium text-gray-900 dark:text-white">{getUnitName(user.unitId)}</p>
                  </div>
                )}
                {user.unitType && (
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Birim Türü</p>
                    <p className="font-medium text-gray-900 dark:text-white">{getUnitTypeName(user.unitType)}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Aktivite Bilgileri</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
              <div>
                <p className="text-gray-500 dark:text-gray-400">Kayıt Tarihi</p>
                <p className="font-medium text-gray-900 dark:text-white">{format(user.createdAt, 'dd MMMM yyyy', { locale: tr })}</p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400">Son Giriş</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {user.lastLogin ? format(user.lastLogin, 'dd MMMM yyyy HH:mm', { locale: tr }) : 'Hiç giriş yapmadı'}
                </p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Yetkiler</h3>
            <div className="flex flex-wrap gap-2">
              {user.permissions && user.permissions.length > 0 ? (
                user.permissions.map((permission, index) => (
                  <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 text-xs rounded-full font-medium">
                    {permission === 'all' ? 'Tüm Yetkiler' : permission.replace('_', ' ')}
                  </span>
                ))
              ) : (
                <span className="text-xs text-gray-500">Özel yetki tanımlanmamış</span>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50">
          <button
            onClick={() => {
              onClose();
              onOpenEdit();
            }}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors"
          >
            <Edit className="w-4 h-4" />
            <span>Düzenle</span>
          </button>
          <button
            onClick={() => {
              onClose();
              onOpenSecurity();
            }}
            className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors"
          >
            <Shield className="w-4 h-4" />
            <span>Güvenlik</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserDetailModal;
